import React, { useEffect, useState } from 'react'
import { Button } from '@material-ui/core'
import { useReactiveVar } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { Plus } from 'react-feather'
import { orderBy } from 'lodash'
import { activeVersionVar, projectVar } from '../../apollo/reactive-vars'
import { ImportHourSeriesDialog } from './ImportHourSeriesDialog'
import { useHourSeriesCollections } from '../../hour-series-collection/use-hour-series-collections'
import { HourSeriesCollectionList } from './HourSeriesCollectionList'
import { useProjects } from '../../project/use-projects'
import {
  HourSeriesCollectionFragment,
  HourSeriesCollectionType,
  HourSeriesFragment,
  HourSeriesType,
  ProjectFragment,
  useProjectCollectionsQuery,
} from '../../generated/graphql'
import { Section } from '../common/Section'
import { useAuthorization } from '../../authorization/use-authorization'
import { useComparisonCalculator } from '../../calculators/use-comparison-calculator'
import { formatDate } from '../common/util/date-util'

export function ProjectHourSeriesPage(): React.ReactElement {
  const [isImportingHourSeries, setIsImportingHourSeries] = useState(false)
  const project = useReactiveVar(projectVar)
  const activeVersion = useReactiveVar(activeVersionVar)
  const {
    createHourSeriesCollections,
    updateHourSeriesCollection,
    deleteHourSeriesCollection,
    isLoading: isHourSeriesMutationLoading,
  } = useHourSeriesCollections()
  const { updateProject, isLoading: isProjectMutationLoading } = useProjects()
  const { t } = useTranslation()
  const canEditProject = useAuthorization().canEditProject(project)
  const { updateComparisonCalculation } = useComparisonCalculator()

  const { data, loading } = useProjectCollectionsQuery({
    variables: { id: project?.id ?? '' },
    skip: !project,
    fetchPolicy: 'cache-and-network',
  })

  useEffect(() => {
    const hourSeriesCollections = data?.project.hourSeriesCollections

    if (hourSeriesCollections && project) {
      const updatedProject: ProjectFragment = {
        ...project,
        hourSeriesCollections,
      }

      projectVar(updatedProject)
    }
    // Only activate this project update when data is updated
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  if (!project || !activeVersion) {
    return <div />
  }

  const setTemperatureHourSeriesIfNecessary = async (
    hourSeriesCollections: HourSeriesCollectionFragment[],
  ) => {
    if (loading) {
      return
    }

    const temperatureHourSeries = hourSeriesCollections
      .flatMap((collection) => collection.hourSeries)
      .find(
        (hourSeries) => hourSeries.type === HourSeriesType.OutdoorTemperature,
      )

    if (temperatureHourSeries && !project.temperatureHourSeries) {
      await updateProject(project.id, {
        temperatureHourSeriesId: temperatureHourSeries.id,
      })
    }
  }

  const onCreateCollections = async (
    hourSeriesFile: File,
    shouldOverwrite: boolean,
  ): Promise<void> => {
    if (loading) {
      return
    }

    const savedCollections = await createHourSeriesCollections(
      hourSeriesFile,
      shouldOverwrite,
      project,
    )

    await setTemperatureHourSeriesIfNecessary(savedCollections)

    updateComparisonCalculation()
  }

  const onDeleteCollection = async (
    collection: HourSeriesCollectionFragment,
  ) => {
    if (loading) {
      return
    }

    await deleteHourSeriesCollection(collection, project)
  }

  const onDeleteHourSeries = async (
    collection: HourSeriesCollectionFragment,
    deletedHourSeries: HourSeriesFragment,
  ) => {
    if (loading) {
      return
    }

    await updateHourSeriesCollection(collection.id, {
      hourSeriesIds: collection.hourSeries
        .map((hourSeries) => hourSeries.id)
        .filter((id) => id !== deletedHourSeries.id),
    })
  }

  const projectSpecificCollections = project.hourSeriesCollections.filter(
    (collection) =>
      collection.type === HourSeriesCollectionType.ProjectSpecific,
  )

  const lastUpdatedAt =
    projectSpecificCollections.length > 0
      ? orderBy(projectSpecificCollections, 'updatedAt', 'desc')[0].updatedAt
      : undefined

  const lastUpdated = lastUpdatedAt
    ? t('projectHourSeries.lastUpdated', {
        time: formatDate(new Date(lastUpdatedAt), t('date.short')),
      })
    : undefined

  const disableEdit = loading || !canEditProject

  return (
    <Section
      title={t('projectHourSeries.title')}
      subtitle={lastUpdated}
      addon={
        <Button
          id="import-hour-series-button"
          variant="contained"
          color="primary"
          size="large"
          startIcon={<Plus />}
          onClick={() => setIsImportingHourSeries(true)}
          disabled={disableEdit}
        >
          {t('projectHourSeries.import')}
        </Button>
      }
    >
      <HourSeriesCollectionList
        collections={projectSpecificCollections}
        onDeleteCollection={onDeleteCollection}
        onDeleteHourSeries={onDeleteHourSeries}
        editDisabled={disableEdit}
      />

      <ImportHourSeriesDialog
        isOpen={isImportingHourSeries}
        isLoading={isHourSeriesMutationLoading || isProjectMutationLoading}
        onClose={() => setIsImportingHourSeries(false)}
        onCreateCollections={onCreateCollections}
      />
    </Section>
  )
}
